import { sha256 } from 'js-sha256';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { runWithRetries } from '../util/misc.js';
import { firstLoraxApiNum, lastFlatApiNum } from './params.js';
import { revisionStringToNumber } from './version-string.js';
/** Keys for user level access */
export const userKeys = {
    flatA: 8469038,
    flatX: 'FUrZc0WilhUBteT2JlCc+A==',
    lorax: 'ZMZFYlbyb1scoSc3pd1x+w==',
};
const unlockA = async (keys, param) => {
    await param.set(keys.flatA);
};
const makeKey = (secretStr, seed) => {
    const seedArray = new Uint8Array(seed);
    const msg = new Uint8Array(32);
    for (let i = 0; i < 16; ++i) {
        msg[i] = secretStr.charCodeAt(i);
        msg[i + 16] = seedArray[i];
    }
    const sha = sha256.create().update(msg).arrayBuffer();
    const key = sha.slice(0, 16);
    return key;
};
const unlockX = async (keys, param) => {
    const seed = await param.readOnce();
    if (!seed) {
        throw 'Could not read seed from device';
    }
    const key = makeKey(atob(keys.flatX), seed);
    await param.set(key, true);
};
const unlockLorax = async (keys, conn) => {
    const seed = await conn.getLoraxAccessSeed();
    if (!seed) {
        throw 'Could not read seed from device';
    }
    const key = makeKey(atob(keys.lorax), seed);
    await conn.loraxUnlockAccess(key);
};
const apiNumA = revisionStringToNumber('A');
const apiNumX = revisionStringToNumber('X');
const getSingletonParam = (reg, key, apiVersion) => {
    const arr = reg.get(key).filter((p) => p.isInApi(apiVersion, undefined));
    if (!arr.length) {
        throw new Error(`No param found for key ${key} with API version ${apiVersion}`);
    }
    if (arr.length > 1) {
        throw new Error(`Multiple params found for key ${key} with API version ${apiVersion}`);
    }
    return arr[0];
};
export class AccessUnlocker {
    conn;
    logger;
    ota;
    /** Keys to be used in unlocking; replace with different values for factory access */
    keys = userKeys;
    unlockedSubj = new BehaviorSubject(undefined);
    subscriptions = [];
    params;
    /**
     * @param { ConnectionManager } conn `ConnectionManager` instance for the connection
     * @param { Logger } logger `Logger` to use for errors
     * @param { OtaManager } ota `OtaManager` instance for the connection
     */
    constructor(conn, logger, ota) {
        this.conn = conn;
        this.logger = logger;
        this.ota = ota;
    }
    /**
     * Connect the ParamRegistry once it's constructed
     * @param { ParamRegistry } params `ParamRegistry` to connect
     */
    setup(params) {
        if (this.params) {
            throw new Error('Multiple calls to AccessUnlocker#setup');
        }
        this.params = {
            apiVersionParamFlat: getSingletonParam(params, 'swApiVersion', lastFlatApiNum),
            apiVersionParamLorax: getSingletonParam(params, 'swApiVersion', firstLoraxApiNum),
            keyParamFlatA: getSingletonParam(params, 'factoryAccessKey', apiNumA),
            keyParamFlatX: getSingletonParam(params, 'accessKey', apiNumX),
        };
        // clear unlocked state when not connected to an device running the application
        this.subscriptions.push(this.ota.isConnectedAppObs
            .pipe(filter((v) => !v))
            .subscribe(() => this.unlockedSubj.next(undefined)));
    }
    /** Close all subscriptions */
    teardown() {
        this.subscriptions.forEach((sub) => sub.unsubscribe());
    }
    /**
     * @returns { UnlockedInfo | undefined } Info if access is unlocked, else undefined
     */
    get unlocked() {
        return this.unlockedSubj.value;
    }
    /**
     * @returns { Observable<UnlockedInfo | undefined> } `Observable` emitting info if access is unlocked, else undefined
     */
    get unlockedObs() {
        return this.unlockedSubj.asObservable();
    }
    /**
     * Unlock a device.
     * @returns Unlocked device API version
     */
    async unlock() {
        if (this.conn.isLoraxConnected) {
            await this.unlockLorax();
        }
        else {
            await this.unlockFlat();
        }
    }
    unlockFlat = () => runWithRetries({
        exec: async () => {
            this.logger.log('Unlocking flat');
            try {
                if (!this.params) {
                    throw new Error('AccessUnlocker not set up');
                }
                const apiVersion = await this.params.apiVersionParamFlat.readOnce();
                if (this.params.keyParamFlatA.isInApi(apiVersion, undefined)) {
                    await unlockA(this.keys, this.params.keyParamFlatA);
                }
                else if (this.params.keyParamFlatX.isInApi(apiVersion, undefined)) {
                    await unlockX(this.keys, this.params.keyParamFlatX);
                }
                else {
                    throw `No unlock key found for API level ${apiVersion ?? 'undefined'}`;
                }
                this.unlockedSubj.next({ apiVersion });
                this.logger.log('Unlocked OK');
                return { apiVersion };
            }
            catch (error) {
                this.logger.warn('unlockFlat() failed', { error });
                this.unlockedSubj.next(undefined);
                throw error;
            }
        },
        maxRetries: 10,
    });
    unlockLorax = () => runWithRetries({
        exec: async () => {
            this.logger.log('Unlocking Lorax');
            try {
                if (!this.params) {
                    throw new Error('AccessUnlocker not set up');
                }
                await unlockLorax(this.keys, this.conn);
                const apiVersion = await this.params.apiVersionParamLorax.readOnce();
                this.unlockedSubj.next({ apiVersion });
                this.logger.log('Unlocked OK');
                return { apiVersion };
            }
            catch (error) {
                this.logger.warn('unlockLorax() failed', { error });
                this.unlockedSubj.next(undefined);
                throw error;
            }
        },
        maxRetries: 10,
    });
}
//# sourceMappingURL=access-unlocker.js.map